import { inject, Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../../../core/auth/auth.service';
import { SupabaseService } from '../../../core/supabase.service';
import { Tables } from '../../../models/enums';
import { Agency, Room } from '../../../models/interfaces';

/**
 * L'agence est rattachée par FK COMPOSITE (agency_id, organisation_id) :
 * PostgREST ne sait pas déduire l'embed, il faut nommer la contrainte.
 */
const ROOM_SELECT = '*, agency:agencies!rooms_agency_same_org(id, label)';

@Injectable({
  providedIn: 'root'
})
export class RoomsService {
  private supabase = inject(SupabaseService);
  private auth = inject(AuthService);

  /** Toutes les salles de l'organisation, actives ou non (réglages, vue « Aujourd'hui »). */
  getRooms(): Observable<Room[]> {
    return from(
      this.supabase.client
        .from(Tables.Rooms)
        .select(ROOM_SELECT)
        .eq('organisation_id', this.auth.getOrganisationId())
        .order('name', { ascending: true })
    ).pipe(
      map(r => {
        if (r.error) throw r.error;
        return r.data as Room[];
      })
    );
  }

  /** Salles réservables : les salles désactivées sont masquées des choix de réservation. */
  getBookableRooms(): Observable<Room[]> {
    return from(
      this.supabase.client
        .from(Tables.Rooms)
        .select(ROOM_SELECT)
        .eq('organisation_id', this.auth.getOrganisationId())
        .eq('is_active', true)
        .order('name', { ascending: true })
    ).pipe(
      map(r => {
        if (r.error) throw r.error;
        return r.data as Room[];
      })
    );
  }

  /** Agences de l'organisation, pour rattacher une salle. */
  getAgencies(): Observable<Pick<Agency, 'id' | 'label'>[]> {
    return from(
      this.supabase.client
        .from(Tables.Agencies)
        .select('id, label')
        .eq('organisation_id', this.auth.getOrganisationId())
        .order('label', { ascending: true })
    ).pipe(
      map(r => {
        if (r.error) throw r.error;
        return r.data as Pick<Agency, 'id' | 'label'>[];
      })
    );
  }

  createRoom(data: Partial<Room>): Observable<Room> {
    return from(
      this.supabase.client
        .from(Tables.Rooms)
        .insert({
          ...data,
          organisation_id: this.auth.getOrganisationId()
        })
        .select(ROOM_SELECT)
        .single()
    ).pipe(
      map(r => {
        if (r.error) throw r.error;
        return r.data as Room;
      })
    );
  }

  updateRoom(id: string, data: Partial<Room>): Observable<Room> {
    return from(
      this.supabase.client
        .from(Tables.Rooms)
        .update(data)
        .eq('id', id)
        .select(ROOM_SELECT)
        .single()
    ).pipe(
      map(r => {
        if (r.error) throw r.error;
        return r.data as Room;
      })
    );
  }

  deleteRoom(id: string): Observable<void> {
    return from(
      this.supabase.client
        .from(Tables.Rooms)
        .delete()
        .eq('id', id)
        .eq('organisation_id', this.auth.getOrganisationId())
    ).pipe(
      map(r => {
        if (r.error) throw r.error;
        return;
      })
    );
  }
}
